/**
 * 67.75: the motor pool's structure, not its play (the owner, Oct 20: "Let's start with 1.  But not really impacting play
 * much yet as we still haven't quite figured out how match start would work with this setup, when vehicles get unpacked
 * etc, but we could probably just figure out structure of motor pool etc before integrating too seriously."). A side's
 * motor pool as a plan on lane F's module grid (MODULE_CELL, 2 people): the compound's walls (BASE_TEMPLATES.motorPool),
 * its frame slots in rows with lanes between them for the stacker arm, a passage through the rows to the gate, the
 * corner robots' bays and the unpack pad on the apron inside the gate (the owner's picks on 1e: *"Ok corner bots and stacker
 * crane are my picks"*). Which frames stand in it: one per vehicle the side has in stock (63.1n), equipment containers for
 * its supplies. Where it stands: behind the side's base, past everything it owns there.
 *
 * Drawn only (rendering/motorPoolPlay.ts, behind the Lab's *Motor pool* presentation row): nothing here is pushed into the
 * state or read by a tick, so the match is bit-identical with it on or off. Pure and deterministic (dmath for the trig).
 */
import type { GameState, MotorPoolLoad, MotorPoolPlan, MotorPoolStack, Vec3, VehicleKind } from '../types';
import { MODULE_CELL } from '../constants/modules';
import { BASE_TEMPLATES, MOTOR_POOL_PARTS } from './modules';
import { VEHICLE_KINDS } from './vehicleStock';
import { baseStoreSpot } from './buildCosts';
import { structureTeam } from './body';
import { structureFootprint } from './buildingScale';
import { footingOf, standOf } from './stands';
import { buildBaseLift } from './buildSeat';
import { TERRACE } from './terraceGround';
import { READY_LINE } from '../constants/action';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub, surfaceTangentFrame } from '../math';
import { dcos, dsin } from './dmath';


/**
 * The plan, 17 × 15 cells (34 × 30 people, ~61 × 54 m at the kit's person):
 *   - the gate south (cells 7–8 of the south wall) onto a passage three cells wide (i 7–9) running north through every row to
 *     the back gate (69.10: the lighters' strip lies behind the pool, and the corner robots bring their frames in by it);
 *   - the apron inside the gate (j 1–3): the unpack pad west of the passage (a frame along z, its ramp south toward the
 *     gate), the corner robots' two bays east of it;
 *   - four rows of slots, four stacks each either side of the passage (a slot three cells along x: a 7.6 m frame and room at
 *     its ends for the corner robots; three high, the stacker arm's reach): row A (j 4), lane 1 (j 5–7), rows B and C back
 *     to back (j 8, 9), lane 2 (j 10–12), row D (j 13) against the north wall;
 *   - the stacker arm's station in lane 1, east of the passage, facing row B. 16 stacks, 48 frames.
 */
/** 69.22: how much farther back the pool's site stands than its terrace's reach, for the coarse mesh the terrace edits (people; bp-3's pad came back to its own ground at 6). */
export const POOL_MESH_MARGIN_PEOPLE = 6;

/**
 * 69.22: the pool's hardstand — the terrace laid under its compound and the ready line before its gate together (people): a
 * square's halves as wide as the compound's footing or the ready line's spots and a 6×6's half, deep from the compound's back to
 * past the ready line's far row and a 6×6's half, its centre that far forward of the compound's (toward `front`, the gate).
 * The ready line had stood on the natural ground past the terrace's band: 16 people of rise under a 6×6 on bp-4's slope.
 */
export function poolHardstand(foot: { halfXPeople: number; halfZPeople: number }): { halfXPeople: number; halfZPeople: number; offsetPeople: number; frontPeople: number } {
  const frontPeople = Math.max(foot.halfZPeople, ...READY_LINE.rowsZ.map(z => -z + 3.5));
  const halfXPeople = Math.max(foot.halfXPeople, ...READY_LINE.xs.map(x => Math.abs(x) + 2.5));
  return { halfXPeople, halfZPeople: (frontPeople + foot.halfZPeople) / 2, offsetPeople: (frontPeople - foot.halfZPeople) / 2, frontPeople };
}

/** 69.22: a structure's skirt as its stand has it — seated where it stands (its lift, or the one the setup will seat it at); 0 without a stand. */
export function skirtOf(state: Pick<GameState, 'planets' | 'config'> & Partial<Pick<GameState, 'turnNumber'>>, s: GameState['structures'][number]): number {
  const lift = s.baseLift ?? buildBaseLift({ ...state, turnNumber: state.turnNumber ?? 0 } as GameState, s.type, s.planetIndex, s.surfaceDir, 0, s.builder);
  if (lift === undefined) return 0;
  return standOf(state, { ...s, baseLift: lift })?.skirt ?? 0;
}

export const MOTOR_POOL: MotorPoolPlan = {
  key: 'motorPool',
  cols: 17,
  rows: 15,
  walls: BASE_TEMPLATES.motorPool,
  ...MOTOR_POOL_PARTS,   // (69.14: in core/modules.ts, which stands the pool's stacks, arm and robots as modules)
};

/**
 * The equipment containers every motor pool starts with (DECISIONS 224 (5): "make that part of the default starting pool"),
 * each packed as constants/logistics.ts CONTAINER_PACKINGS: the base's stores (its convoy stock, its dumps' and FOBs' crates),
 * the build kits, the ammunition, the medical stores, the fuel bladder.
 */
export const MOTOR_POOL_SUPPLIES: readonly MotorPoolLoad[] = [
  { container: 'stores' }, { container: 'kits' }, { container: 'ammo' }, { container: 'medical' }, { container: 'fuel' },
];

/** How many of a side's vehicles of a kind stand (alive) now. */
function fielded(state: Pick<GameState, 'structures' | 'players'>, team: number, kind: VehicleKind): number {
  let n = 0;
  for (const s of state.structures) if (s.type === kind && s.health > 0 && structureTeam(state, s) === team) n++;
  return n;
}

/**
 * What a side's motor pool holds: a frame for every vehicle in its stock (63.1n's `left`, kind by kind in VEHICLE_KINDS's
 * order) — with the stock off, a stand-in of one spare for every vehicle it fields — then its supplies' containers.
 */
export function motorPoolLoads(state: Pick<GameState, 'structures' | 'players' | 'vehicleStocks'> & Partial<Pick<GameState, 'lighters' | 'tick'>>, team: number): MotorPoolLoad[] {
  const stock = state.vehicleStocks?.[team], out: MotorPoolLoad[] = [];
  // 69.10 step 3: a frame a lighter brought stands in the yard once the robots have stacked it; till then it is on the strip's apron or in their hands (in the stock already)
  const staged = state.lighters?.staged?.[team], tick = state.tick ?? 0;
  for (const kind of VEHICLE_KINDS) {
    const held = staged ? staged.filter(f => f.kind === kind && f.stacked > tick).length : 0;
    const n = Math.max(0, (stock ? stock[kind]?.left ?? 0 : fielded(state, team, kind)) - held);
    for (let k = 0; k < n; k++) out.push({ vehicle: kind });
  }
  return [...out, ...MOTOR_POOL_SUPPLIES];
}

/**
 * The loads in their places, level by level: the ground tier of every slot in the plan's order, then the second, then
 * the third; what the plan can't hold is left out (`over` counts it).
 */
export function fillMotorPool(plan: MotorPoolPlan, loads: readonly MotorPoolLoad[]): { stacks: MotorPoolStack[]; over: number } {
  const stacks: MotorPoolStack[] = [], most = Math.max(0, ...plan.slots.map(s => s.tiers));
  let k = 0;
  for (let tier = 0; tier < most && k < loads.length; tier++) {
    for (let slot = 0; slot < plan.slots.length && k < loads.length; slot++) if (tier < plan.slots[slot].tiers) stacks.push({ slot, tier, load: loads[k++] });
  }
  return { stacks, over: loads.length - k };
}

/** A cell rectangle's centre in the plan's frame (people; the origin at the compound's ground centre, x along cols, z along rows). */
export function planCentre(plan: Pick<MotorPoolPlan, 'cols' | 'rows'>, i: number, j: number, cols = 1, rows = 1): { x: number; z: number } {
  return { x: (i + cols / 2 - plan.cols / 2) * MODULE_CELL, z: (j + rows / 2 - plan.rows / 2) * MODULE_CELL };
}

/** Where a side's people stand (their centre, on its first unit's planet): its home where it has no base (a skirmish without HQ or landing). */
function peopleSpot(state: Pick<GameState, 'players'>, team: number): { planetIndex: number; surfaceDir: Vec3 } | null {
  const lead = state.players.filter(p => p.teamIndex === team).sort((a, b) => a.id - b.id)[0];
  if (!lead) return null;
  let sum: Vec3 = { x: 0, y: 0, z: 0 };
  for (const p of state.players) if (p.teamIndex === team && p.planetIndex === lead.planetIndex) sum = vecAdd(sum, vecNormalize(p.surfaceDir));
  return vecLength(sum) > 1e-9 ? { planetIndex: lead.planetIndex, surfaceDir: vecNormalize(sum) } : null;
}

/**
 * Where a side's motor pool stands: behind its base (its HQ, else its board base, else its landing, else its people) on the side away
 * from the enemy, its gate toward them, past the farthest of the side's own structures there and its people by its
 * footprint and four people. `dir` the compound's centre (a unit vector from the planet's centre), `front` the tangent there
 * its gate faces. Null where the side has no base.
 */
export function motorPoolSite(state: Pick<GameState, 'structures' | 'players' | 'board' | 'actionHomes' | 'planets' | 'config'> & Partial<Pick<GameState, 'turnNumber'>>, team: number, plan: Pick<MotorPoolPlan, 'rows'> = MOTOR_POOL): { planetIndex: number; dir: Vec3; front: Vec3 } | null {
  const home = baseStoreSpot(state, team) ?? peopleSpot(state, team), planet = home ? state.planets[home.planetIndex] : undefined;
  if (!home || !planet) return null;
  const R = planet.radius, n = vecNormalize(home.surfaceDir), person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const on = state.players.filter(p => p.alive && p.planetIndex === home.planetIndex);
  let toFoe: Vec3 = { x: 0, y: 0, z: 0 };
  for (const p of on) if (p.teamIndex !== team) toFoe = vecAdd(toFoe, vecNormalize(p.surfaceDir));
  toFoe = vecSub(toFoe, vecScale(n, vecDot(toFoe, n)));
  const back = vecLength(toFoe) > 1e-9 ? vecScale(vecNormalize(toFoe), -1) : surfaceTangentFrame(n).tx;
  // how far back the side's own things reach (an arc along `back`, near enough at a base's size)
  let reach = 0;
  for (const s of state.structures) {
    if (s.planetIndex !== home.planetIndex || s.health <= 0 || s.type === 'motorPool' || structureTeam(state, s) !== team) continue;   // (63.16 (7): the compound stands where this put it — it never pushes its own site, nor the strip's behind it)
    // (63.16 (h): past a seated building's deck — its stand's diagonal, whichever way it is turned — not its hitbox's footprint, which
    // left the compound's front wall across the HQ's deck corner on most seeds. 69.14: the footing's own diagonal — the stand's,
    // seated or not, so the site is the same before the decks are seated (where the compound's terrace is laid) and after)
    // (69.22: and its skirt — a deck on a slope ramps its skirt out past its footing, up to 19 people round bp-4's HQ, and the
    // compound stood on it — as its stand has it seated where it is: its own lift, or the lift it will be seated at)
    const foot = footingOf(s, state.config), past = foot ? Math.sqrt(foot.halfXPeople * foot.halfXPeople + foot.halfZPeople * foot.halfZPeople) * person + skirtOf(state, s) : structureFootprint(s, state.config, R);
    reach = Math.max(reach, vecDot(vecNormalize(s.surfaceDir), back) * R + past);
  }
  for (const p of on) if (p.teamIndex === team) reach = Math.max(reach, vecDot(vecNormalize(p.surfaceDir), back) * R + person);
  // 69.14: past the reach by the compound's own plinth (its footing's half), its terrace's batter band and a person — the terrace
  // (layPoolTerraces) then holds no deck of the base, which is seated on it after (it was the walls' half and four people: the
  // plinth's skirt ramped over the HQ's and the pad's decks, burying them up to 4.6 and 17 people)
  // (a deeper plan — the lighter strip's yard behind the pool — keeps its depth and the same extra, so it moves back with the pool)
  const own = footingOf({ type: 'motorPool' }, state.config), poolHalf = MOTOR_POOL.rows * MODULE_CELL / 2;
  // (69.22: and POOL_MESH_MARGIN_PEOPLE more — the terrace's edit moves the coarse mesh's triangles past its own reach, so a deck a
  // person past the band was still seated on the ground it raised: bp-3's pad and HQ)
  // (69.22: past the hardstand's front too — the ready line before the gate is laid level with the compound, poolHardstand)
  const ownHalf = own ? Math.max(own.halfXPeople, own.halfZPeople) : poolHalf;
  const extra = Math.max(ownHalf, ownHalf ? poolHardstand({ halfXPeople: ownHalf, halfZPeople: ownHalf }).frontPeople : 0) + TERRACE.bandPeople + 1 + POOL_MESH_MARGIN_PEOPLE - (poolHalf + 4);
  const u = (reach + (plan.rows * MODULE_CELL / 2 + 4 + extra) * person) / R;
  const dir = vecNormalize(vecAdd(vecScale(n, dcos(u)), vecScale(back, dsin(u))));
  const front = vecNormalize(vecSub(vecScale(n, dsin(u)), vecScale(back, dcos(u))));   // (−d/du of dir: back toward the base)
  return { planetIndex: home.planetIndex, dir, front };
}
